const REDACTED_VALUE = "[redacted]";

const HEADER_NAME_FIELD_PATTERN = /^(?:name|key|header)$/;
const HEADER_VALUE_FIELD_PATTERN = /^(?:value|values)$/;
const SAFE_TOKEN_METRIC_PATTERN =
  /^(?:(?:input|output|prompt|completion|cached|reasoning|total)-)?tokens?-(?:count|used|usage)$/;
const SENSITIVE_KEY_SEGMENTS = new Set([
  "auth",
  "authorization",
  "cookie",
  "cookies",
  "credential",
  "credentials",
  "passphrase",
  "password",
  "secret",
  "secrets",
  "token",
  "tokens",
]);
const SENSITIVE_COMPACT_KEYS = new Set([
  "accesstoken",
  "apikey",
  "authtoken",
  "clientsecret",
  "privatekey",
  "refreshtoken",
  "sessiontoken",
]);
const SENSITIVE_KEY_PAIR_PATTERN =
  /(?:^|-)(?:access|api|auth|client|encryption|private|secret|session|signing)-keys?(?:$|-)/;
const AUTHORIZATION_VALUE_PATTERN = /^(?:bearer|basic)\s+\S+/i;
const JWT_VALUE_PATTERN =
  /^[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{8,}$/;
const PEM_PRIVATE_KEY_PATTERN = /-----BEGIN(?: [A-Z0-9]+)* PRIVATE KEY-----/i;
const PREFIXED_SECRET_VALUE_PATTERNS = [
  /^sk-(?:proj-|ant-)?[A-Za-z0-9_-]{16,}$/,
  /^(?:rk_live_|sk_live_|whsec_)[A-Za-z0-9_-]{16,}$/,
  /^(?:github_pat_|gh[pousr]_|glpat-|npm_|hf_)[A-Za-z0-9_-]{16,}$/,
  /^xox[baprs]-[A-Za-z0-9-]{16,}$/,
  /^AIza[A-Za-z0-9_-]{20,}$/,
  /^(?:AKIA|ASIA)[A-Z0-9]{16}$/,
] as const;

function normalizeKey(key: string): string {
  return key
    .trim()
    .replace(/([a-z\d])([A-Z])/g, "$1-$2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1-$2")
    .replace(/[^A-Za-z\d]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
}

function isSensitiveKey(key: string): boolean {
  const normalized = normalizeKey(key);
  if (!normalized || SAFE_TOKEN_METRIC_PATTERN.test(normalized)) return false;
  if (SENSITIVE_COMPACT_KEYS.has(normalized)) return true;
  if (SENSITIVE_KEY_PAIR_PATTERN.test(normalized)) return true;
  return normalized
    .split("-")
    .some((segment) => SENSITIVE_KEY_SEGMENTS.has(segment));
}

function isHeaderCollectionKey(key: string): boolean {
  return /(?:^|-)headers?(?:$|-)/.test(normalizeKey(key));
}

function isSensitiveLiteral(value: string): boolean {
  const trimmed = value.trim();
  return (
    AUTHORIZATION_VALUE_PATTERN.test(trimmed) ||
    JWT_VALUE_PATTERN.test(trimmed) ||
    PEM_PRIVATE_KEY_PATTERN.test(trimmed) ||
    PREFIXED_SECRET_VALUE_PATTERNS.some((pattern) => pattern.test(trimmed))
  );
}

function paramsContainSensitiveData(params: URLSearchParams): boolean {
  for (const [key, value] of params) {
    if (isSensitiveKey(key) || isSensitiveLiteral(value)) return true;
  }
  return false;
}

function stringContainsSensitiveUrlMaterial(value: string): boolean {
  if (
    !value.includes("?") &&
    !value.includes("#") &&
    !/^[a-z][a-z\d+.-]*:\/\//i.test(value) &&
    !value.startsWith("//")
  ) {
    return false;
  }
  try {
    const url = new URL(value, "https://coogo.invalid");
    if (url.username || url.password) return true;
    if (paramsContainSensitiveData(url.searchParams)) return true;

    const fragment = url.hash.slice(1);
    if (!fragment) return false;
    if (isSensitiveLiteral(fragment)) return true;
    const fragmentCandidates = [
      fragment,
      ...(fragment.includes("?")
        ? [fragment.slice(fragment.indexOf("?") + 1)]
        : []),
    ];
    return fragmentCandidates.some((candidate) =>
      paramsContainSensitiveData(new URLSearchParams(candidate)),
    );
  } catch {
    return false;
  }
}

function isSensitiveString(value: string): boolean {
  return isSensitiveLiteral(value) || stringContainsSensitiveUrlMaterial(value);
}

function isBinaryValue(value: unknown): boolean {
  return (
    (typeof File !== "undefined" && value instanceof File) ||
    (typeof Blob !== "undefined" && value instanceof Blob)
  );
}

function isSensitiveHeaderTuple(
  value: unknown,
  insideHeaderCollection: boolean,
): value is [string, unknown] {
  return (
    insideHeaderCollection &&
    Array.isArray(value) &&
    value.length === 2 &&
    typeof value[0] === "string" &&
    isSensitiveKey(value[0])
  );
}

function sensitiveHeaderValueFields(
  value: Record<string, unknown>,
): Set<string> {
  const headerName = Object.entries(value).find(
    ([key, entry]) =>
      HEADER_NAME_FIELD_PATTERN.test(normalizeKey(key)) &&
      typeof entry === "string" &&
      isSensitiveKey(entry),
  );
  if (!headerName) return new Set();
  return new Set(
    Object.keys(value).filter((key) =>
      HEADER_VALUE_FIELD_PATTERN.test(normalizeKey(key)),
    ),
  );
}

export function containsStudioOrchestrationSensitiveData(
  value: unknown,
  key = "",
  insideHeaderCollection = false,
): boolean {
  const nextHeaderCollection =
    insideHeaderCollection || isHeaderCollectionKey(key);
  if (isSensitiveKey(key) || isBinaryValue(value)) return true;
  if (typeof value === "string") return isSensitiveString(value);
  if (isSensitiveHeaderTuple(value, nextHeaderCollection)) return true;
  if (Array.isArray(value)) {
    return value.some((entry) =>
      containsStudioOrchestrationSensitiveData(entry, "", nextHeaderCollection),
    );
  }
  if (!value || typeof value !== "object") return false;
  const record = value as Record<string, unknown>;
  if (sensitiveHeaderValueFields(record).size > 0) return true;
  return Object.entries(record).some(([entryKey, entry]) =>
    containsStudioOrchestrationSensitiveData(
      entry,
      entryKey,
      nextHeaderCollection,
    ),
  );
}

export function isStudioOrchestrationValueSafe(value: unknown): boolean {
  return !containsStudioOrchestrationSensitiveData(value);
}

export function sanitizeStudioOrchestrationValue(
  value: unknown,
  key = "",
  insideHeaderCollection = false,
): unknown {
  const nextHeaderCollection =
    insideHeaderCollection || isHeaderCollectionKey(key);
  if (isSensitiveKey(key) || isBinaryValue(value)) return REDACTED_VALUE;
  if (typeof value === "string") {
    return isSensitiveString(value) ? REDACTED_VALUE : value;
  }
  if (isSensitiveHeaderTuple(value, nextHeaderCollection)) {
    return [value[0], REDACTED_VALUE];
  }
  if (Array.isArray(value)) {
    return value.map((entry) =>
      sanitizeStudioOrchestrationValue(entry, "", nextHeaderCollection),
    );
  }
  if (!value || typeof value !== "object") return value;
  const record = value as Record<string, unknown>;
  const headerValueFields = sensitiveHeaderValueFields(record);
  return Object.fromEntries(
    Object.entries(record).map(([entryKey, entry]) => [
      entryKey,
      isSensitiveKey(entryKey) || headerValueFields.has(entryKey)
        ? REDACTED_VALUE
        : sanitizeStudioOrchestrationValue(
            entry,
            entryKey,
            nextHeaderCollection,
          ),
    ]),
  );
}
